/**
 * Admin Page Component
 * Form to upload new eBooks to the platform
 */

import React, { useState, useEffect } from 'react';
import { addEbook, fetchEbooks, deleteEbook } from '../api';

const AdminPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    downloadLink: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [isDragging, setIsDragging] = useState(false);
  const [ebooks, setEbooks] = useState([]);
  const [loadingEbooks, setLoadingEbooks] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load ebooks on component mount
  useEffect(() => {
    loadEbooks();
  }, []);

  // Load ebooks from API
  const loadEbooks = async () => {
    try {
      setLoadingEbooks(true);
      const response = await fetchEbooks();
      if (response.success) {
        setEbooks(response.data);
      }
    } catch (error) {
      console.error('Error loading ebooks:', error);
    } finally {
      setLoadingEbooks(false);
    }
  };

  // Handle delete ebook
  const handleDelete = async (ebookId, ebookTitle) => {
    if (deleting) return;

    try {
      setDeleting(true);
      const response = await deleteEbook(ebookId);

      if (response.success) {
        setMessage(`✅ eBook "${ebookTitle}" deleted successfully`);
        setMessageType('success');
        setDeleteConfirm(null);
        // Reload ebooks list
        await loadEbooks();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      setMessage(`❌ Failed to delete eBook`);
      setMessageType('error');
    } finally {
      setDeleting(false);
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    processFile(file);
  };

  // Process file
  const processFile = (file) => {
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      setMessage('Please enter eBook title');
      setMessageType('error');
      return;
    }

    if (!formData.downloadLink.trim()) {
      setMessage('Please enter download link (Google Drive link)');
      setMessageType('error');
      return;
    }

    // Validate image file
    if (imageFile) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(imageFile.type)) {
        setMessage('Please select a valid image file (JPEG, PNG, GIF, WebP)');
        setMessageType('error');
        return;
      }

      if (imageFile.size > 5 * 1024 * 1024) { // 5MB
        setMessage('Image file size must be less than 5MB');
        setMessageType('error');
        return;
      }
    }

    try {
      setLoading(true);
      setMessage('');

      // Validate download link URL
      try {
        new URL(formData.downloadLink);
      } catch {
        setMessage('Invalid download link. Please enter a valid URL.');
        setMessageType('error');
        setLoading(false);
        return;
      }

      // Create FormData for multipart upload
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('downloadLink', formData.downloadLink);

      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      // Send form data to backend
      const response = await addEbook(formDataToSend);
      console.log('Upload response:', response);

      if (response && response.success) {
        setMessage(`✅ eBook "${formData.title}" added successfully!`);
        setMessageType('success');

        // Clear form
        setFormData({
          title: '',
          description: '',
          downloadLink: '',
        });
        setImageFile(null);
        setImagePreview(null);

        // Clear file input
        const fileInput = document.getElementById('image');
        if (fileInput) fileInput.value = '';

        // Reload ebooks list
        await loadEbooks();

        // Auto-clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ Failed to add eBook: ${response?.message || 'Unknown error'}`);
        setMessageType('error');
      }
    } catch (error) {
      console.error('Full error object:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to add eBook';
      setMessage(`❌ ${errorMsg}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', marginTop: '1rem' }}>
        <a
          href="/"
          className="back-button"
          title="Back to home page"
        >
          ← Back to Home
        </a>
      </div>

      <h1 className="page-title">🛠️ Admin Panel - Upload Magazine</h1>

      <div className="admin-container">
        <form onSubmit={handleSubmit} className="admin-form">
          {/* Title Field */}
          <div className="form-group">
            <label htmlFor="title">
              📖 Magazine Title (required) <br />
              <small>Tamil or English</small>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., தமிழ் விஞ்ஞானக்கதைகள்"
              disabled={loading}
            />
          </div>

          {/* Description Field */}
          <div className="form-group">
            <label htmlFor="description">
              📝 Description (optional)
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter detailed description about the Magazine..."
              disabled={loading}
            ></textarea>
          </div>

          {/* Image Upload Field */}
          <div className="form-group">
            <label htmlFor="image">
              🖼️ Cover Image (optional) <br />
              <small>JPEG, PNG, GIF, WebP (max 5MB)</small>
            </label>
            
            {!imagePreview ? (
              <div
                className={`upload-zone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={loading}
                  style={{ display: 'none' }}
                />
                <div style={{ textAlign: 'center', pointerEvents: 'none' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📸</div>
                  <p style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Drop image here or click to select
                  </p>
                  <p style={{ fontSize: '0.85rem', color: '#999' }}>
                    Supported: JPEG, PNG, GIF, WebP (max 5MB)
                  </p>
                </div>
                <button
                  type="button"
                  className="upload-btn"
                  onClick={() => document.getElementById('image').click()}
                  disabled={loading}
                >
                  Choose Image
                </button>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <small style={{ color: '#666', display: 'block', marginBottom: '1rem' }}>
                  Image preview:
                </small>
                <img
                  src={imagePreview}
                  alt="preview"
                  style={{
                    maxHeight: '200px',
                    maxWidth: '100%',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    objectFit: 'cover',
                    marginBottom: '1rem'
                  }}
                />
                <button
                  type="button"
                  className="upload-btn remove-btn"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                    const fileInput = document.getElementById('image');
                    if (fileInput) fileInput.value = '';
                  }}
                  disabled={loading}
                >
                  ✕ Remove Image
                </button>
              </div>
            )}
          </div>

          {/* Download Link Field */}
          <div className="form-group">
            <label htmlFor="downloadLink">
              🔗 Download Link (required) <br />
              <small>Google Drive sharing link or direct PDF URL</small>
            </label>
            <input
              type="url"
              id="downloadLink"
              name="downloadLink"
              value={formData.downloadLink}
              onChange={handleChange}
              placeholder="https://drive.google.com/file/d/..."
              disabled={loading}
              required
            />
          </div>

          {/* Message Display */}
          {message && (
            <div className="form-group">
              <div className={messageType === 'success' ? 'success-message' : 'error-message'}>
                {message}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="btn btn-submit"
            disabled={loading}
          >
            {loading ? (
              <div className="spinner"></div>
            ) : (
              '📤 Upload Magazine'
            )}
          </button>
        </form>

        {/* Instructions */}
        <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f9f9f9', borderRadius: '8px' }}>
          <h3>📋 Instructions:</h3>
          <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
            <li>Enter the Magazine title in Tamil or English</li>
            <li>Add a description to help users understand the content</li>
            <li>Provide a cover image URL (optional but recommended)</li>
            <li>Upload your PDF to Google Drive and share the link</li>
            <li>To get Google Drive link:
              <ul>
                <li>Right-click the file → Share</li>
                <li>Change to "Anyone with the link can view"</li>
                <li>Copy the link and paste here</li>
              </ul>
            </li>
          </ul>
        </div>

        {/* Existing Magazines Management */}
        <div style={{ marginTop: '3rem', padding: '1.5rem', background: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
          <h3>📚 Existing Magazines</h3>
          
          {loadingEbooks && <div className="spinner" style={{ marginTop: '2rem' }}></div>}
          
          {!loadingEbooks && ebooks.length === 0 && (
            <p style={{ color: '#999', marginTop: '1rem' }}>No magazines available yet</p>
          )}

          {!loadingEbooks && ebooks.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#f9f9f9' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Title</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Downloads</th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ebooks.map((ebook) => (
                    <tr key={ebook.id} style={{ borderBottom: '1px solid #eee', hover: { backgroundColor: '#f9f9f9' } }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: '600' }}>{ebook.title}</div>
                        <small style={{ color: '#666' }}>{ebook.description}</small>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>-</td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <button
                          type="button"
                          className="upload-btn remove-btn"
                          onClick={() => setDeleteConfirm(ebook)}
                          disabled={deleting}
                          style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal active" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">⚠️ Confirm Delete</h2>
              <button
                className="close-btn"
                onClick={() => !deleting && setDeleteConfirm(null)}
                disabled={deleting}
              >
                ×
              </button>
            </div>
            <p style={{ marginBottom: '1.5rem', fontSize: '1rem', color: '#333' }}>
              Are you sure you want to delete <strong>"{deleteConfirm.title}"</strong>?<br />
              <small style={{ color: '#999' }}>This action cannot be undone.</small>
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                style={{ maxWidth: '120px' }}
              >
                Cancel
              </button>
              <button
                className="upload-btn remove-btn"
                onClick={() => handleDelete(deleteConfirm.id, deleteConfirm.title)}
                disabled={deleting}
                style={{ maxWidth: '120px' }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
